import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import imgDivider from '@/assets/svg/ai-enhancement-header-divider.svg';
import imgAIIntro from '@/assets/svg/ai-enhancement-intro.svg';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { AsyncSelect } from '@/ui/select/async/async-select';
import { sharedProps } from '@/ui/select/select-shared';
import { BorderedTextArea } from '@/ui/textarea/bordered-text-area';
import { Tooltip } from '@/ui/tooltip/tooltip';

import { TEXTAREA_MAX_LENGTH } from '../constants/enhance.constants';
import {
  FieldLabel,
  OverflowContainer,
  ProblemDefinitionIntroLogo,
} from '../styles/enhance-modal.styles';

const FORM_FIELD_HINT_TOOLTIP_PROPS = {
  hideOnClick: false,
  maxWidth: '250px',
  padding: '32px',
  placement: 'top-end' as const,
  showOnFocused: true,
  showOnHovered: false,
  theme: 'sky' as const,
  visible: true,
  withArrow: true,
};

interface FormFieldHintTooltipProps {
  children: ReactElement;
  content: ReactNode;
  disabled?: boolean;
}

const FormFieldHintTooltip = ({
  children,
  content,
  disabled,
}: FormFieldHintTooltipProps) => (
  <Tooltip
    content={content}
    disabled={disabled}
    {...FORM_FIELD_HINT_TOOLTIP_PROPS}
  >
    {children}
  </Tooltip>
);

type LocationOption = { label: string; value: string };

interface ProblemDefinitionViewProps {
  dataTestId?: string;
  desiredSolution: string;
  locations: LocationOption[];
  onDesiredSolutionChange: (value: string) => void;
  onLocationsChange: (locations: LocationOption[]) => void;
  onProblemDescriptionChange: (value: string) => void;
  problemDescription: string;
  setLocationOptions: (search: string) => Promise<LocationOption[]>;
  showTooltips?: boolean;
}

export const ProblemDefinitionView = ({
  dataTestId = 'problem-definition-view',
  desiredSolution,
  locations,
  onDesiredSolutionChange,
  onLocationsChange,
  onProblemDescriptionChange,
  problemDescription,
  setLocationOptions,
  showTooltips = false,
}: ProblemDefinitionViewProps) => {
  const { t } = useTranslation('projects');

  const isStatementOverflow = problemDescription.length > TEXTAREA_MAX_LENGTH;

  const problemTextarea = (
    <BorderedTextArea
      id="problem-description-textarea"
      height={showTooltips ? 141 : 120}
      maxHeight={showTooltips ? 392 : 240}
      resize="vertical"
      maxLength={TEXTAREA_MAX_LENGTH}
      placeholder={t`enhanceConfigQuestionObjectivePlaceholder`}
      value={problemDescription}
      onChange={onProblemDescriptionChange}
      error={showTooltips ? isStatementOverflow : false}
    />
  );

  const desiredSolutionTextarea = (
    <BorderedTextArea
      id="desired-solution-textarea"
      height={showTooltips ? 141 : 120}
      maxHeight={showTooltips ? 392 : 240}
      resize="vertical"
      maxLength={TEXTAREA_MAX_LENGTH}
      placeholder={t`enhanceConfigQuestionDesiredSolutionPlaceholder`}
      value={desiredSolution}
      onChange={onDesiredSolutionChange}
    />
  );

  return (
    <>
      {!showTooltips && (
        <>
          <img src={imgDivider} alt="" />
          <Flexbox gap="24px" alignItems="flex-start">
            <Stack gap="8px" style={{ flex: 1 }}>
              <span>{t`autoEnhanceIntroText1`}</span>
              <span>{t`autoEnhanceIntroText2`}</span>
            </Stack>
            <ProblemDefinitionIntroLogo src={imgAIIntro} alt="auto-enhance" />
          </Flexbox>
        </>
      )}

      <OverflowContainer
        name="problem-definition-body"
        data-testid={dataTestId}
        direction="column"
        gap="18px"
      >
        <Flexbox direction="column" name="problem-statement-section" gap="8px">
          <FieldLabel>{t`enhanceConfigQuestionObjective`}</FieldLabel>
          {showTooltips ? (
            <FormFieldHintTooltip
              content={t`enhanceConfigQuestionObjectivePlaceholder`}
              disabled={problemDescription.length === 0}
            >
              {problemTextarea}
            </FormFieldHintTooltip>
          ) : (
            problemTextarea
          )}
          {showTooltips && (
            <ProblemStatementCounter
              isError={isStatementOverflow}
            >{`${problemDescription.length}/${TEXTAREA_MAX_LENGTH}`}</ProblemStatementCounter>
          )}
        </Flexbox>

        <Flexbox
          direction="column"
          name="desired-solution-section"
          gap="8px"
          margin={showTooltips ? '0px 0px 20px 0px' : '0px'}
        >
          <FieldLabel>{t`enhanceConfigQuestionDesiredSolution`}</FieldLabel>
          {showTooltips ? (
            <FormFieldHintTooltip
              content={t`enhanceConfigQuestionDesiredSolutionPlaceholder`}
              disabled={desiredSolution.length === 0}
            >
              {desiredSolutionTextarea}
            </FormFieldHintTooltip>
          ) : (
            desiredSolutionTextarea
          )}
        </Flexbox>

        <Stack gap="16px" maxWidth={showTooltips ? '100%' : '480px'}>
          <FieldLabel>{t`enhanceConfigQuestionLocation`}</FieldLabel>
          <AsyncSelect
            id="location-select"
            dataTestId="location-dropdown"
            isMulti
            isSearchable
            hideSelectedOptions
            setOptions={setLocationOptions}
            onChange={option => {
              if (Array.isArray(option)) onLocationsChange(option);
            }}
            {...sharedProps}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={220}
            value={locations}
          />
        </Stack>
      </OverflowContainer>
    </>
  );
};

const ProblemStatementCounter = styled('p')<{ isError: boolean }>`
  ${({ theme }) => theme.typography.widget.smallText}
  color: ${({ isError, theme }) =>
    isError ? theme.colors.system.lowFit : theme.colors.gray.c12};
  display: flex;
  justify-content: end;
`;
